import { observable, computed, action } from "mobx";
import moment from "moment";
import { EditableFile } from "./SharedFilesEditDialog";

type OpenOptions = {
  closeOnSuccess?: boolean; // skip "Files updated" screen
  files?: EditableFile[];
  onSubmit: (files: EditableFile[], expirationDate: Date) => Promise<any>;
  onClose?: (uploadedFile: EditableFile[] | null) => void;
};

type EditStatus = "initial" | "editing" | "success" | "fail";

export class SharedFileEditStore {
  @observable private _options: OpenOptions | null = null;
  @observable private _files: EditableFile[] = [];
  @observable private _editStatus: EditStatus = "initial";
  @observable private _expirationDays = 0;

  @computed get canSubmit() {
    const fileNameHasChanged = this._files.some((item, i) => {
      return item.name !== this._options.files[i].name;
    });
    const noName = this._files.every((item) => item.name.length === 0);

    if (noName) {
      return false;
    }

    if (!fileNameHasChanged && !this._expirationDays) {
      return false;
    }

    return true;
  }

  @computed get files() {
    return this._files;
  }

  @computed get isOpen() {
    return this._files.length > 0;
  }

  @computed get isEditing(): boolean {
    return this._editStatus === "editing";
  }

  @computed get editStatus() {
    return this._editStatus;
  }

  @computed get expirationDate() {
    return this._expirationDays === 0 ? "" : this._expirationDays.toString();
  }

  @action.bound close() {
    const onClose = this._options.onClose;

    if (onClose) {
      onClose(this.editStatus === "success" ? this._files : null);
    }

    this._editStatus = "initial";
    this._expirationDays = 0;
    this._files = [];
  }

  @action.bound setName(idx: number, name: string) {
    this._files[idx].name = name;
  }

  @action.bound setExpirationDate(v: string) {
    if (!v) {
      return;
    }

    this._expirationDays = parseInt(v);
  }

  @action.bound open(options: OpenOptions) {
    this._options = options;
    this._files = options.files;

    if (!options.files || options.files.length === 0) {
      return this.close();
    }
  }

  @action.bound submit() {
    if (!this._files || !this._options || !this._options.onSubmit) {
      return;
    }

    if (this.isEditing) {
      return;
    }

    this._editStatus = "editing";

    const expDate =
      this._expirationDays > 0
        ? moment().add(this.expirationDate, "days").toDate()
        : null;

    this._options
      .onSubmit(this._files, expDate)
      .then(() => {
        if (this._options.closeOnSuccess) {
          return this.close();
        }

        this._editStatus = "success";
        // show success
      })
      .catch((e) => {
        this._editStatus = "fail";
        // show error
      });
  }
}
